"use client";

import { useReadContract } from "wagmi";
import { STAKE_ABI } from "@/contract/abi/stake";
import ContractAddress from "@/contract/addresses";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";

/**
 * Fast totals for the top metric grid (NFT count + total DNM staked +
 * total UVM staked). Derives directly from `getUserStake` — no per-stake
 * `calculateReward` reads, so this hook resolves with one RPC round-trip
 * (~300ms) instead of waiting for the full reward computation that
 * `useFetchStakeContractData` does (3 calls × N stakes).
 *
 * The full reward hook still computes these same totals as a side-effect,
 * but it gates them behind the slow path. This hook is a parallel
 * lightweight reader so the top yellow boxes can render fast.
 */
export function useStakedTotals() {
  const address = useEffectiveAddress();

  const { data: user_stake_list, isLoading } = useReadContract({
    abi: STAKE_ABI,
    functionName: "getUserStake",
    args: address ? [address] : undefined,
    query: { enabled: !!address },
    address: ContractAddress.STAKE,
  });

  let nftCount = 0;
  let totalDnmStakedWei = 0n;
  let totalUvmStakedWei = 0n;

  if (user_stake_list?.length) {
    for (const stake of user_stake_list) {
      if (stake.exists && stake.finish === 0n) {
        nftCount += 1;
        totalDnmStakedWei += stake.dnm;
        totalUvmStakedWei += stake.uvm;
      }
    }
  }

  return {
    nftCount,
    totalDnmStakedWei,
    totalUvmStakedWei,
    isLoading,
  };
}
